// Global state to track which component is active
let activeComponent = null;

// Z-index constants for popups and overlays
const Z_INDEX = {
  SEARCH_POPUP: 1010, // Above overlay (1001)
  OVERLAY_SEARCH: 1001, // Above header
  CART_DRAWER: 1200,
  OVERLAY_CART_DRAWER: 1195,
  MOBILE_MENU_DRAWER: 1002,
  MOBILE_MENU_OVERLAY: 1001,
  DEFAULT_OVERLAY: 999, // Base overlay z-index
};

// Overlay z-index management
const setOverlayZIndex = (zIndex) => {
  const mainOverlay = document.querySelector(".main__overlay");
  if (mainOverlay) {
    mainOverlay.style.zIndex = zIndex;
  }
};

// Reset overlay z-index to default
const resetOverlayZIndex = () => {
  const mainOverlay = document.querySelector(".main__overlay");
  if (mainOverlay) {
    mainOverlay.style.zIndex = Z_INDEX.DEFAULT_OVERLAY;
  }
};

// Helper function to close other popups
const closeOtherPopups = (excludeComponent) => {
  if (activeComponent === "search" && excludeComponent !== "search") {
    closeSearch();
  }
  if (activeComponent === "cart" && excludeComponent !== "cart") {
    closeCartDrawer();
  }
  if (activeComponent === "menu" && excludeComponent !== "menu") {
    toggleMenu();
  }
  if (
    activeComponent === "localization" &&
    excludeComponent !== "localization"
  ) {
    if (document.body.classList.contains("has-localization")) {
      closeLocalization("close");
    }
  }
};

// ============================================================================
// SEARCH POPUP (Header Search)
// ============================================================================

const openSearch = () => {
  const searchForm = document.getElementById("header__search");
  const searchInput = searchForm?.querySelector(".header__search-input");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!searchForm || searchForm.classList.contains("active")) return;

  // Close other popups
  closeOtherPopups("search");

  // Lock header
  if (window.HeaderControl) {
    HeaderControl.lock();
  }

  // Prepare search UI
  searchForm.style.transition = "all 0.3s ease";

  if (document.body.style.overflow !== "hidden") {
    document.body.style.overflow = "hidden";
  }

  // Set overlay z-index for search
  setOverlayZIndex(Z_INDEX.OVERLAY_SEARCH);

  // Adjust overlay position (below header)
  if (window.adjustOverlay) {
    adjustOverlay();
  }

  // Use requestAnimationFrame for smoother animation
  requestAnimationFrame(() => {
    searchForm.classList.add("active");
    mainOverlay.classList.add("active");
    if (searchInput) {
      searchInput.focus();
    }
    activeComponent = "search";

    // Kaleido: don't close on overlay click - only close button / Escape
    const isKaleido = searchForm.classList.contains("header__search-kaleido");
    if (!isKaleido) {
      const overlayClickHandler = (e) => {
        if (e.target === mainOverlay) {
          closeSearch(false);
        }
      };
      mainOverlay.addEventListener("click", overlayClickHandler);
      searchForm._overlayHandler = overlayClickHandler;
    }

    // Kaleido: show recommendations on open
    if (isKaleido) {
      const ps = searchForm.querySelector("predictive-search-kaleido");
      if (ps && typeof ps.showRecommendations === "function") {
        ps.showRecommendations();
      }
    }
  });
};

const closeSearch = (mouseInHeader = false) => {
  const searchForm = document.getElementById("header__search");
  const searchInput = searchForm?.querySelector(".header__search-input");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!searchForm || !searchForm.classList.contains("active")) return;

  // Reset search UI
  searchForm.style.transition = "none";
  searchForm.classList.remove("active");
  mainOverlay.classList.remove("active");

  if (searchInput) {
    searchInput.value = "";
  }

  // Reset overlay position
  if (window.resetOverlay) {
    resetOverlay();
  }

  // Reset overlay z-index
  resetOverlayZIndex();

  document.body.style.overflow = "";

  // Remove overlay click handler
  if (searchForm._overlayHandler) {
    mainOverlay.removeEventListener("click", searchForm._overlayHandler);
    searchForm._overlayHandler = null;
  }

  // Unlock and restore header state
  if (window.HeaderControl) {
    HeaderControl.unlock();
  }

  activeComponent = null;
};

// ============================================================================
// MOBILE MENU
// ============================================================================

const toggleMenu = () => {
  const mobileMenuBtn = document.getElementById("mobile-menu-btn");
  const mobileMenuDropdown = document.getElementById("mobile-menu-dropdown");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!mobileMenuBtn || !mobileMenuDropdown) return;

  const isOpening = !mobileMenuBtn.classList.contains("active");

  mobileMenuBtn.classList.toggle("active");
  mobileMenuDropdown.classList.toggle("active");

  if (isOpening) {
    // Lock header
    if (window.HeaderControl) {
      HeaderControl.lock();
    }

    // Close other popups
    closeOtherPopups("menu");

    // Set overlay z-index for mobile menu
    setOverlayZIndex(Z_INDEX.MOBILE_MENU_OVERLAY);

    // Add overlay click handler when opening
    const overlayClickHandler = (e) => {
      if (e.target === mainOverlay) {
        toggleMenu();
      }
    };
    mainOverlay.addEventListener("click", overlayClickHandler);
    mobileMenuDropdown._overlayHandler = overlayClickHandler;

    // Set active component
    activeComponent = "menu";
  } else {
    // Unlock header
    if (window.HeaderControl) {
      HeaderControl.unlock();
    }

    // Remove overlay click handler when closing
    if (mobileMenuDropdown._overlayHandler) {
      mainOverlay.removeEventListener(
        "click",
        mobileMenuDropdown._overlayHandler
      );
      mobileMenuDropdown._overlayHandler = null;
    }

    // Reset overlay z-index
    resetOverlayZIndex();

    activeComponent = null;
  }

  // Toggle overlay
  if (mainOverlay.classList.contains("active")) {
    mainOverlay.classList.remove("active");
  } else {
    mainOverlay.classList.add("active");
  }

  document.body.style.overflow = mobileMenuBtn.classList.contains("active")
    ? "hidden"
    : "";
};

// ============================================================================
// CART DRAWER
// ============================================================================

const openCartDrawer = () => {
  const cartDrawer = document.getElementById("cart-drawer");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!cartDrawer || cartDrawer.classList.contains("open")) return;

  // Close other popups
  closeOtherPopups("cart");

  cartDrawer.classList.add("open");
  mainOverlay.classList.add("active");

  // Set overlay z-index for cart drawer
  setOverlayZIndex(Z_INDEX.OVERLAY_CART_DRAWER);

  activeComponent = "cart";

  if (document.body.style.overflow !== "hidden") {
    document.body.style.overflow = "hidden";
  }

  // Lock header
  if (window.HeaderControl) {
    HeaderControl.lock();
  }

  // Add overlay click handler
  const overlayClickHandler = (e) => {
    if (e.target === mainOverlay) {
      closeCartDrawer();
    }
  };
  mainOverlay.addEventListener("click", overlayClickHandler);
  cartDrawer._overlayHandler = overlayClickHandler;
};

const closeCartDrawer = () => {
  const cartDrawer = document.getElementById("cart-drawer");
  const mainOverlay = document.querySelector(".main__overlay");

  if (!cartDrawer || !cartDrawer.classList.contains("open")) return;

  cartDrawer.classList.remove("open");
  mainOverlay.classList.remove("active");

  // Reset overlay z-index
  resetOverlayZIndex();

  document.body.style.overflow = "";
  activeComponent = null;

  // Remove overlay click handler
  if (cartDrawer._overlayHandler) {
    mainOverlay.removeEventListener("click", cartDrawer._overlayHandler);
    cartDrawer._overlayHandler = null;
  }

  // Unlock and restore header state
  if (window.HeaderControl) {
    HeaderControl.unlock();
  }
};

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener("DOMContentLoaded", () => {
  const header = document.getElementById("main-header");

  // Mobile menu functionality
  const mobileMenuBtn = document.getElementById("mobile-menu-btn");
  if (mobileMenuBtn) {
    mobileMenuBtn.addEventListener("click", toggleMenu);
  }

  // Search functionality
  const searchBtn = document.getElementById("search-btn");
  if (searchBtn) {
    searchBtn.addEventListener("click", () => {
      if (activeComponent === "search") {
        closeSearch();
      } else {
        openSearch();
      }
    });
  }

  // Close search when clicking outside (header/announcement)
  document.addEventListener("mousedown", (e) => {
    if (activeComponent !== "search") return;

    const searchForm = document.getElementById("header__search");
    const isKaleido = searchForm?.classList.contains("header__search-kaleido");

    const searchElements = [
      document.querySelector(".header__search-container"),
      document.getElementById("search-btn"),
      searchForm,
      // Kaleido: entire popup is "inside" - never close on click inside
      document.querySelector(".search-kaleido-popup"),
      document.querySelector(".predictive-search-kaleido-wrap"),
    ];

    const clickedInsideSearch = searchElements.some(
      (el) => el && (el === e.target || el.contains(e.target))
    );

    // Kaleido: only close via close button or Escape, not outside click
    if (isKaleido) return;

    if (!clickedInsideSearch) {
      closeSearch(header?.contains(e.target));
    }
  });

  // Escape key handler
  const handleEscapeKey = (e) => {
    if (e.key === "Escape") {
      if (activeComponent === "menu") {
        toggleMenu();
      } else if (activeComponent === "search") {
        const isMouseOverHeaderNow = header?.matches(":hover");
        closeSearch(isMouseOverHeaderNow);
      } else if (activeComponent === "cart") {
        closeCartDrawer();
      } else if (activeComponent === "video-popup") {
        if (window.closeVideoPopup) {
          window.closeVideoPopup();
        }
      } else if (activeComponent === "filter") {
        if (window.closeFilterDrawer) {
          window.closeFilterDrawer();
        }
      } else if (activeComponent === "localization") {
        closeLocalization("close");
      }
    }
  };

  // Add escape key listener
  document.addEventListener("keydown", handleEscapeKey);

  document.querySelectorAll(".localization-button").forEach((btn) => {
    btn.addEventListener("click", openLocalization);
  });

  // Make functions available globally
  window.openCartDrawer = openCartDrawer;
  window.closeCartDrawer = closeCartDrawer;
  window.openSearch = openSearch;
  window.closeSearch = closeSearch;
  window.toggleMenu = toggleMenu;
  window.activeComponent = activeComponent;

  // Export overlay management functions
  window.setOverlayZIndex = setOverlayZIndex;
  window.resetOverlayZIndex = resetOverlayZIndex;
  window.Z_INDEX = Z_INDEX;
});

// ============================================================================
// HELPER FUNCTIONS (for other popups)
// ============================================================================

const closeAllPopups = (excludeComponent) => {
  if (excludeComponent !== "search") {
    closeSearch();
  }
  if (excludeComponent !== "cart") {
    closeCartDrawer();
  }
  if (excludeComponent !== "menu") {
    const mobileMenuBtn = document.getElementById("mobile-menu-btn");
    if (mobileMenuBtn?.classList.contains("active")) {
      toggleMenu();
    }
  }
  if (excludeComponent !== "video-popup" && window.closeVideoPopup) {
    window.closeVideoPopup();
  }
  if (excludeComponent !== "filter" && window.closeFilterDrawer) {
    window.closeFilterDrawer();
  }
  if (
    excludeComponent !== "localization" &&
    document.body.classList.contains("has-localization")
  ) {
    closeLocalization("close");
  }
};

// Legacy support for filter drawer (if exists elsewhere)
if (typeof window.openFilterDrawer === "undefined") {
  window.openFilterDrawer = () => {
    const filterDrawer = document.querySelector(".filters-drawer");
    const overlay = document.querySelector(".main__overlay");
    if (!filterDrawer) return;

    closeOtherPopups("filter");

    filterDrawer.classList.add("open");
    document.body.style.overflow = "hidden";
    if (overlay) {
      overlay.classList.add("active");
      const overlayClickHandler = (e) => {
        if (e.target === overlay) {
          window.closeFilterDrawer();
        }
      };
      overlay.addEventListener("click", overlayClickHandler);
      filterDrawer._overlayHandler = overlayClickHandler;
    }
    activeComponent = "filter";

    if (window.HeaderControl) {
      HeaderControl.lock();
    }
  };
}

if (typeof window.closeFilterDrawer === "undefined") {
  window.closeFilterDrawer = () => {
    const filterDrawer = document.querySelector(".filters-drawer");
    const overlay = document.querySelector(".main__overlay");
    if (!filterDrawer) return;

    filterDrawer.classList.remove("open");
    document.body.style.overflow = "";
    if (overlay) {
      overlay.classList.remove("active");
      if (filterDrawer._overlayHandler) {
        overlay.removeEventListener("click", filterDrawer._overlayHandler);
        filterDrawer._overlayHandler = null;
      }
    }
    resetOverlayZIndex();
    activeComponent = null;

    if (window.HeaderControl) {
      HeaderControl.unlock();
    }
  };
}

// ============================================================================
// LOCALIZATION
// ============================================================================

const LOCALIZATION_TIMER_KEY = "localizationTimer";
const LOCALIZATION_TIMER_DURATION = 360000; // 1 hour in milliseconds
let localizationId;

// Open localization menu
const openLocalization = () => {
  clearTimeout(localizationId);
  const localizationMenu = document.getElementById("localization-menu");
  if (!localizationMenu) {
    console.log("localizationMenu not found in open");
    return;
  }

  if (document.body.classList.contains("has-localization")) {
    closeLocalization();
  } else {
    // Reset the timer whenever manually opened
    localStorage.removeItem(LOCALIZATION_TIMER_KEY);

    activeComponent = "localization";
    closeAllPopups("localization");
    localizationMenu.classList.remove("hiding");
    document.body.classList.add("has-localization");

    // Update button state
    const localizationButton = document.getElementById("localization-button");
    if (localizationButton) {
      localizationButton.setAttribute("aria-expanded", "true");
    }

    localizationMenu.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest",
    });
  }
};

// Close localization menu
const closeLocalization = (type) => {
  const localizationMenu = document.getElementById("localization-menu");
  if (!localizationMenu) {
    console.log("localizationMenu not found in close");
    return;
  }

  // Start the timer when closed
  localStorage.setItem(LOCALIZATION_TIMER_KEY, Date.now().toString());

  const localizationButton = document.getElementById("localization-button");
  if (localizationButton) {
    localizationButton.setAttribute("aria-expanded", "false");
  }

  if (type === "close") {
    localizationMenu.classList.add("hiding");
  }

  localizationId = setTimeout(() => {
    document.body.classList.remove("has-localization");
  }, 300);

  activeComponent = null;
};

// Make functions available globally
window.openLocalization = openLocalization;
window.closeLocalization = closeLocalization;
